import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import { toast } from 'react-toastify';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import { API_BASE_URL } from '../../../config/api';

const COOPendingGoals = () => {
    const baseUrl = API_BASE_URL;
    const [loading, setLoading] = useState(false);
    const [goals, setGoals] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 20;
    const [expandedTexts, setExpandedTexts] = useState({});
    
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedGoal, setSelectedGoal] = useState(null);
    
    const [approvalLoading, setApprovalLoading] = useState(null);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectGoal, setRejectGoal] = useState(null);
    const [rejectSubmitting, setRejectSubmitting] = useState(false);
    const [rejectError, setRejectError] = useState(null);

    const handleViewGoal = (goal) => {
        setSelectedGoal(goal);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setSelectedGoal(null);
    };

    const toggleTextExpansion = (goalId, field) => {
        setExpandedTexts(prev => ({
            ...prev,
            [`${goalId}-${field}`]: !prev[`${goalId}-${field}`]
        }));
    };

    const renderTruncatedText = (text, goalId, field, maxLength = 40) => {
        const safeText = (text ?? '').toString();
        if (!safeText) return '';
        if (safeText.length <= maxLength) return safeText;

        const key = `${goalId || 'unknown'}-${field}`;
        const isExpanded = !!expandedTexts[key];
        const shownText = isExpanded ? safeText : `${safeText.slice(0, maxLength)}...`;

        return (
            <span>
                <span style={{ whiteSpace: 'pre-wrap' }}>{shownText}</span>
                <button
                    type="button"
                    onClick={() => toggleTextExpansion(goalId || 'unknown', field)}
                    className="btn btn-link btn-sm p-0 ms-2 text-decoration-none fw-semibold"
                >
                    {isExpanded ? 'Read less' : 'Read more'}
                </button>
            </span>
        );
    };

    const fetchPendingGoals = async () => {
        setLoading(true);
        try {
            const token = Cookies.get('token');
            const response = await axios.get(`${baseUrl}/api/goals/pending/`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            const pendingGoals = Array.isArray(response.data) ? response.data.filter(goal => goal.approval_status === 'pending') : [];
            setGoals(pendingGoals);
        } catch (err) {
            toast.error('Failed to fetch pending goals.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPendingGoals();
    }, []);

    const handleApproveGoal = async (goal) => {
        setApprovalLoading(goal.id);
        try {
            const token = Cookies.get('token');
            await axios.post(`${baseUrl}/api/goals/${goal.id}/approval/`, { action: 'approve' }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            toast.success('Goal approved!');
            fetchPendingGoals();
        } catch (err) {
            console.log(err);
            toast.error(err.message || 'Failed to approve goal');
        } finally {
            setApprovalLoading(null);
        }
    };

    const openRejectModal = (goal) => {
        setRejectGoal(goal);
        setRejectReason('');
        setRejectError(null);
        setRejectModalOpen(true);
    };

    const closeRejectModal = () => {
        setRejectModalOpen(false);
        setRejectGoal(null);
        setRejectReason('');
        setRejectError(null);
    };

    const handleRejectSubmit = async (e) => {
        e.preventDefault();
        setRejectSubmitting(true);
        setRejectError(null);
        try {
            const token = Cookies.get('token');
            await axios.post(`${baseUrl}/api/goals/${rejectGoal.id}/approval/`, { action: 'reject', reason: rejectReason }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            toast.success('Goal rejected!');
            fetchPendingGoals();
            closeRejectModal();
        } catch (err) {
            setRejectError(err.message || 'Failed to reject goal');
        } finally {
            setRejectSubmitting(false);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [goals, searchTerm]);

    const searchedGoals = goals.filter(goal => {
        const query = searchTerm.trim().toLowerCase();
        if (!query) return true;
        const employeeName = (goal?.employee_name || '').toString().toLowerCase();
        const fallbackName = `${goal?.first_name || ''} ${goal?.last_name || ''}`.trim().toLowerCase();
        return employeeName.includes(query) || fallbackName.includes(query);
    });

    const totalPages = Math.ceil(searchedGoals.length / rowsPerPage) || 1;
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentGoals = searchedGoals.slice(indexOfFirstRow, indexOfLastRow);

    const getKraLabel = (goal) => {
        const name = goal?.key_result_area?.name || goal?.name || goal?.key_result_area;
        return (name ?? 'Uncategorized').toString();
    };

    const sortedCurrentGoals = [...currentGoals].sort((a, b) => {
        const an = (a?.employee_name || '').toString();
        const bn = (b?.employee_name || '').toString();
        const empCompare = an.localeCompare(bn, undefined, { sensitivity: 'base' });
        if (empCompare !== 0) return empCompare;

        const ak = getKraLabel(a);
        const bk = getKraLabel(b);
        const kraCompare = ak.localeCompare(bk, undefined, { sensitivity: 'base' });
        if (kraCompare !== 0) return kraCompare;

        const aa = (a?.activity || '').toString();
        const ba = (b?.activity || '').toString();
        return aa.localeCompare(ba, undefined, { sensitivity: 'base' });
    });

    const currentPageCountsByEmployee = sortedCurrentGoals.reduce((acc, g) => {
        const key = (g?.employee_name || 'Unknown Employee').toString();
        acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {});

    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > totalPages) return;
        setCurrentPage(newPage);
    };

    const renderApprovalBadge = (status) => {
        switch (status?.toLowerCase()) {
            case "approved":
                return <span className="badge bg-success-subtle text-success border border-success-subtle">Approved</span>;
            case "rejected":
                return <span className="badge bg-danger-subtle text-danger border border-danger-subtle">Rejected</span>;
            case "pending":
                return (
                    <span className="badge bg-warning-subtle text-warning border border-warning-subtle d-inline-flex align-items-center gap-1">
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        Pending
                    </span>
                );
            default:
                return <span className="badge bg-secondary-subtle text-secondary">{status || "Unknown"}</span>;
        }
    };

    return (
        <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
            <Topbar />
            <Sidebar />

            <div className="page-wrapper">
                <div className="content pb-0">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                        <div>
                            <h4 className="fw-bold mb-1">Pending KPIs</h4>
                            <p className="text-muted mb-0 fs-13">View and manage pending KPIs from your department.</p>
                        </div>
                    </div>

                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <div className="row g-3 align-items-center">
                                <div className="col-md-6">
                                    <div className="position-relative">
                                        <input
                                            type="text"
                                            className="form-control ps-5"
                                            placeholder="Search by employee name..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            disabled={loading}
                                        />
                                        <i className="ti ti-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-16" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card shadow-sm mb-4">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="text-muted fs-13 mt-2 mb-0">Loading pending KPIs...</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Activity</th>
                                            <th>Stakeholder</th>
                                            <th style={{ width: "120px" }}>RAG</th>
                                            <th>Quarter</th>
                                            <th>Metric</th>
                                            <th>Status</th>
                                            <th className="text-center" style={{ width: "140px" }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {searchedGoals.length === 0 ? (
                                            <tr>
                                                <td colSpan={7} className="text-center py-5 text-muted">
                                                    <i className="ti ti-target-off fs-36 d-block mb-2 text-secondary opacity-50" />
                                                    No pending KPIs found.
                                                </td>
                                            </tr>
                                        ) : (
                                            sortedCurrentGoals.map((goal, idx) => {
                                                const employeeName = (goal?.employee_name || 'Unknown Employee').toString();
                                                const prevEmployeeName = idx > 0
                                                    ? (sortedCurrentGoals[idx - 1]?.employee_name || 'Unknown Employee').toString()
                                                    : null;
                                                const showEmployeeHeader = idx === 0 || employeeName !== prevEmployeeName;

                                                const kraLabel = getKraLabel(goal);
                                                const prevKraLabel = idx > 0 ? getKraLabel(sortedCurrentGoals[idx - 1]) : null;
                                                const showKraHeader = showEmployeeHeader || kraLabel !== prevKraLabel;

                                                return (
                                                    <React.Fragment key={goal.id || `${employeeName}-${idx}`}>
                                                        {showEmployeeHeader && (
                                                            <tr className="bg-light fw-bold">
                                                                <td colSpan={7} className="py-2 text-dark">
                                                                    <i className="ti ti-user me-2 text-primary"></i>
                                                                    {employeeName}
                                                                    <span className="badge bg-secondary ms-2 fw-semibold">
                                                                        {currentPageCountsByEmployee[employeeName]} pending
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        )}

                                                        {showKraHeader && (
                                                            <tr className="bg-light bg-opacity-50 fw-semibold">
                                                                <td colSpan={7} className="py-2 text-dark ps-4">
                                                                    KRA: <span className="text-primary">{kraLabel}</span>
                                                                    {(() => {
                                                                        const kraPercentage = goal?.key_result_area?.percentage ?? goal?.kra?.percentage ?? goal?.percentage;
                                                                        return kraPercentage !== undefined && kraPercentage !== null ? (
                                                                            <span className="badge bg-secondary ms-2 fw-semibold">
                                                                                Weight: {kraPercentage}%
                                                                            </span>
                                                                        ) : null;
                                                                    })()}
                                                                </td>
                                                            </tr>
                                                        )}

                                                        <tr>
                                                            <td className="ps-4">
                                                                {renderTruncatedText(goal.activity, goal.id || `${idx + indexOfFirstRow}`, 'activity', 55)}
                                                            </td>
                                                            <td>{goal.stakeholder}</td>
                                                            <td>
                                                                <span className="badge w-100 text-white" style={{ backgroundColor: goal.progress_indicator || '#6c757d' }}>
                                                                    {goal.rag_color || 'N/A'}
                                                                </span>
                                                            </td>
                                                            <td>{goal.quarter}</td>
                                                            <td>{goal.metric}%</td>
                                                            <td>{renderApprovalBadge(goal.approval_status)}</td>
                                                            <td className="text-center">
                                                                <div className="d-flex align-items-center justify-content-center gap-1">
                                                                    <button className="btn btn-sm btn-icon btn-light text-primary" title="View" onClick={() => handleViewGoal(goal)}>
                                                                        <i className="ti ti-eye" />
                                                                    </button>
                                                                    {goal.approval_status === 'pending' && (
                                                                        <>
                                                                            <button 
                                                                                className="btn btn-sm btn-icon btn-light text-success" 
                                                                                title="Approve" 
                                                                                onClick={() => handleApproveGoal(goal)}
                                                                                disabled={approvalLoading === goal.id}
                                                                            >
                                                                                {approvalLoading === goal.id ? (
                                                                                    <div className="spinner-border spinner-border-sm" role="status" />
                                                                                ) : (
                                                                                    <i className="ti ti-check" />
                                                                                )}
                                                                            </button>
                                                                            <button 
                                                                                className="btn btn-sm btn-icon btn-light text-danger" 
                                                                                title="Reject" 
                                                                                onClick={() => openRejectModal(goal)}
                                                                            >
                                                                                <i className="ti ti-x" />
                                                                            </button>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    </React.Fragment>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        
                        {/* Pagination */}
                        {!loading && searchedGoals.length > 0 && (
                            <div className="card-footer d-flex align-items-center justify-content-between py-3">
                                <span className="text-muted fs-13">
                                    Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, searchedGoals.length)} of {searchedGoals.length} entries
                                </span>
                                <nav>
                                    <ul className="pagination pagination-sm mb-0">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                                                Previous
                                            </button>
                                        </li>
                                        <li className="page-item disabled">
                                            <span className="page-link text-dark">Page {currentPage} of {totalPages}</span>
                                        </li>
                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                                                Next
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        )}
                    </div>
                </div>

                <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                    <p className="text-dark mb-0">
                        {new Date().getFullYear()} © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
                    </p>
                </div>
            </div>

            {/* View Goal Modal */}
            {modalOpen && selectedGoal && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content shadow-lg border-0">
                            <div className="modal-header">
                                <h5 className="modal-title fw-bold">KPI Details</h5>
                                <button type="button" className="btn-close" onClick={closeModal} aria-label="Close" />
                            </div>
                            <div className="modal-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label text-muted fs-13 mb-1">Key Result Area</label>
                                        <p className="fw-medium text-dark">{selectedGoal.key_result_area?.name || selectedGoal.name || 'N/A'}</p>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label text-muted fs-13 mb-1">Quarter</label>
                                        <p className="fw-medium text-dark">{selectedGoal.quarter || 'N/A'}</p>
                                    </div>
                                    <div className="col-md-12">
                                        <label className="form-label text-muted fs-13 mb-1">Activity</label>
                                        <p className="fw-medium text-dark bg-light p-2 rounded">{selectedGoal.activity || 'N/A'}</p>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label text-muted fs-13 mb-1">Stakeholder</label>
                                        <p className="fw-medium text-dark">{selectedGoal.stakeholder || 'N/A'}</p>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label text-muted fs-13 mb-1">Completion Status</label>
                                        <p className="fw-medium text-dark">
                                            {selectedGoal.status ? selectedGoal.status.charAt(0).toUpperCase() + selectedGoal.status.slice(1).replace(/_/g, ' ') : 'N/A'}
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label text-muted fs-13 mb-1">RAG Status</label>
                                        <div>
                                            <span className="badge w-100 text-white p-2" style={{ backgroundColor: selectedGoal.progress_indicator || '#e5e7eb' }}>
                                                {selectedGoal.rag_color || 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label text-muted fs-13 mb-1">Approval Status</label>
                                        <div>{renderApprovalBadge(selectedGoal.approval_status)}</div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label text-muted fs-13 mb-1">Is Overdue</label>
                                        <p className={`fw-medium ${selectedGoal.is_overdue ? 'text-danger' : 'text-success'}`}>
                                            {selectedGoal.is_overdue ? 'Yes' : 'No'}
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label text-muted fs-13 mb-1">Due Date</label>
                                        <p className="fw-medium text-dark">
                                            {selectedGoal.completion_date ? new Date(selectedGoal.completion_date).toLocaleDateString('en-GB') : 'N/A'}
                                        </p>
                                    </div>
                                    {selectedGoal.approval_status === 'rejected' && selectedGoal.rejected_reason && (
                                        <div className="col-md-12">
                                            <label className="form-label text-danger fs-13 mb-1">Rejection Reason</label>
                                            <p className="fw-medium text-danger bg-danger-subtle p-2 rounded">{selectedGoal.rejected_reason}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeModal}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Goal Modal */}
            {rejectModalOpen && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content shadow-lg border-0">
                            <form onSubmit={handleRejectSubmit}>
                                <div className="modal-header">
                                    <h5 className="modal-title fw-bold text-danger">Reject KPI</h5>
                                    <button type="button" className="btn-close" onClick={closeRejectModal} aria-label="Close" />
                                </div>
                                <div className="modal-body">
                                    {rejectError && (
                                        <div className="alert alert-danger p-2 fs-14 mb-3">
                                            <i className="ti ti-alert-circle me-1" /> {rejectError}
                                        </div>
                                    )}
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Rejection Reason <span className="text-danger">*</span></label>
                                        <textarea
                                            className="form-control"
                                            value={rejectReason}
                                            onChange={e => setRejectReason(e.target.value)}
                                            required
                                            placeholder="Enter reason for rejection..."
                                            rows={4}
                                            disabled={rejectSubmitting}
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-light" onClick={closeRejectModal} disabled={rejectSubmitting}>Cancel</button>
                                    <button type="submit" className="btn btn-danger" disabled={rejectSubmitting}>
                                        {rejectSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Rejecting...
                                            </>
                                        ) : 'Reject KPI'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default COOPendingGoals;